<template>
  <div v-if="scan" class="scan-details-dialog">
    <div class="scan-header">
      <div>
        <div class="eyebrow">Scan Details</div>
        <h2 class="scan-title">{{ scan.name || "Unnamed Scan" }}</h2>
        <p class="scan-subtitle">
          Review scan progress, metadata, and available actions.
        </p>
      </div>

      <div class="status-wrap">
        <ScanStatusPill :status="scan.status" />
      </div>
    </div>

    <v-row class="mt-2" dense>
      <v-col cols="12" md="7">
        <div class="section-card hero-card mb-4">
          <div class="section-header">
            <span class="section-title">Processing Overview</span>
          </div>

          <div class="hero-message">
            <div class="hero-message-title">{{ getScanStatusText(scan.status) }}</div>
            <div class="hero-message-body">
              {{ getScanStatusDescription(scan.status) }}
            </div>
          </div>

          <div v-if="canGenerate" class="mt-4">
            <v-btn class="mint-btn" block :loading="modelGenerationButtonClicked" :disabled="modelGenerationButtonClicked" @click="startGeneration">
              Queue for Processing
            </v-btn>
          </div>
        </div>

        <div class="section-card mb-4">
          <div class="section-header">
            <span class="section-title">Key Metrics</span>
          </div>

          <div class="metrics-grid">
            <div class="metric-card">
              <div class="metric-label">Capture Type</div>
              <div class="metric-value">{{ captureTypeLabel(scan) }}</div>
            </div>

            <div class="metric-card">
              <div class="metric-label">Frames Generated</div>
              <div class="metric-value">{{ getStageClass('overall') === 'stage-success' ? displayNumber(scan.numberOfFrames) : '-' }}</div>
            </div>

            <div class="metric-card">
              <div class="metric-label">Current Status</div>
              <div class="metric-value">{{ getNormalizedStatus(scan.status) || "Unknown" }}</div>
            </div>

            <div class="metric-card">
              <div class="metric-label">Created By</div>
              <div class="metric-value">{{ scan.createdBy || "Unknown" }}</div>
            </div>

            <div class="metric-card">
              <div class="metric-label">Last Updated</div>
              <div class="metric-value">{{ formatDate(scan.statusUpdatedOn) }}</div>
            </div>
          </div>
        </div>

        <div class="section-card">
          <div class="section-header">
            <span class="section-title">Timeline</span>
          </div>

          <div class="timeline-list">
            <div class="timeline-row">
              <span class="timeline-label">Created On</span>
              <span class="timeline-value">{{ formatDate(scan.createdOn) }}</span>
            </div>

            <div class="timeline-row">
              <span class="timeline-label">Status Updated</span>
              <span class="timeline-value">{{ formatDate(scan.statusUpdatedOn) }}</span>
            </div>

            <div class="timeline-row">
              <span class="timeline-label">Asset Id</span>
              <span class="timeline-value mono">{{ scan.assetId || "—" }}</span>
            </div>

            <div class="timeline-row">
              <span class="timeline-label">Scan Id</span>
              <span class="timeline-value mono">{{ scan.id || "—" }}</span>
            </div>
          </div>
        </div>
      </v-col>

      <v-col cols="12" md="5">
        <div class="section-card mb-4">
          <div class="section-header">
            <span class="section-title">Pipeline Stages</span>
          </div>

          <div class="stage-list">
            <div class="stage-row">
              <span class="stage-name">Source File Upload</span>
              <span class="stage-badge" :class="getStageClass('upload')">
                {{ getStageText('upload') }}
              </span>
            </div>

            <div class="stage-row">
              <span class="stage-name">Source File Processing</span>
              <span class="stage-badge" :class="getStageClass('source-processing')">
                {{ getStageText('source-processing') }}
              </span>
            </div>

            <div class="stage-row">
              <span class="stage-name">Pipeline Video</span>
              <span class="stage-badge" :class="getStageClass('pipeline-video')">
                {{ getStageText('pipeline-video') }}
              </span>
            </div>

            <div class="stage-row">
              <span class="stage-name">Model Generation</span>
              <span class="stage-badge" :class="getStageClass('model-generation')">
                {{ getStageText('model-generation') }}
              </span>
            </div>

            <div class="stage-row">
              <span class="stage-name">AI Analysis</span>
              <span class="stage-badge" :class="getStageClass('ai-analysis')">
                {{ getStageText('ai-analysis') }}
              </span>
            </div>

            <div class="stage-row">
              <span class="stage-name">Overall Scan</span>
              <span class="stage-badge" :class="getStageClass('overall')">
                {{ getStageText('overall') }}
              </span>
            </div>
          </div>
        </div>

        <div class="section-card mb-4">
          <div class="section-header">
            <span class="section-title">Available Actions</span>
          </div>

          <div class="actions-stack">
            <v-btn v-if="canGenerate" class="mint-btn" :loading="modelGenerationButtonClicked" :disabled="modelGenerationButtonClicked" block @click="startGeneration">
              Queue for Processing
            </v-btn>

            <!-- Duallo staff only, while the scan is parked in awaiting-duallo-review:
                 jump to the AI review screen. The accept/reject verdict itself lives
                 there and in the admin review queue — not in this dialog. -->
            <v-btn v-if="isAwaitingReview && canDualloAdmin" class="review-inspect-btn" block @click="goToAiReview">
              Review AI Detections
            </v-btn>

            <!-- Duallo staff only: download the master pipeline video (video-master/video.mp4) once
                 the scan is past model generation (AI review or later) — handy for reviewing captures. -->
            <v-btn v-if="canDownloadMasterVideo" class="download-master-btn" :loading="downloadingMaster"
              :disabled="downloadingMaster" block @click="downloadMasterVideo">
              <v-icon start size="18">mdi-download</v-icon>
              Download Master Video
            </v-btn>

            <v-btn class="cancel-btn" block @click="closeWindow">
              Close
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ScanStatusPill from '../pills/ScanStatusPill.vue';
import { useConfirm } from '@/composable/useConfirm';
import ModelGenerationRequest from '@/domain/scan/ModelGenerationRequest';
import ModelVersionService from '@/services/ModelVersionService';
import organizationSasService from '@/services/OrganizationSasService';
import { useOrganizationStore } from '@/store/organizationStore';
import { Permissions } from '@/constants/permissions';
import { usePermissions } from '@/composable/usePermissions';
import { AWAITING_DUALLO_REVIEW, getScanStatusDescription, getScanStatusText, sourceUploadStage, sourceProcessingStage, pipelineVideoStage, modelGenerationStage, aiAnalysisStage, overallStage, stageClass } from '@/utils/ScanStatus';

export default {
  name: "ScanDetailsDialog",
  props: {
    scan: {
      type: Object,
      default: null,
    }
  },
  components: {
    ScanStatusPill
  },
  setup() {
    const { can } = usePermissions();
    return { can };
  },
  data() {
    return {
      organizationStore: null,
      modelGenerationButtonClicked: false,
      downloadingMaster: false
    };
  },
  mounted() {
    this.organizationStore = useOrganizationStore();
  },
  emits: ['OnRefresh', 'onCloseWindow', 'onGenerationStarted'],
  computed: {
    canGenerate() {
      return this.scan.status === "source-file-uploaded-success";
    },
    canDualloAdmin() {
      return this.can(Permissions.DualloAdminView);
    },
    isAwaitingReview() {
      return this.scan?.status === AWAITING_DUALLO_REVIEW;
    },
    // Duallo staff only, and only once the scan is past model generation (model-generation-success
    // onward: AI analysis, awaiting review, scan complete) — i.e. "beyond model generation in
    // progress". The master pipeline video exists by then, so allow downloading it for review.
    canDownloadMasterVideo() {
      return this.canDualloAdmin
        && !!this.scan
        && modelGenerationStage(this.scan.status) === 'Complete';
    },
  },
  methods: {
    emitRefresh() {
      this.$emit('OnRefresh');
    },

    getScanStatusText(status) {
      return getScanStatusText(status, this.canDualloAdmin);
    },

    getScanStatusDescription(status) {
      return getScanStatusDescription(status, this.canDualloAdmin);
    },

    goToAiReview() {
      this.$router.push({ name: 'ProvenanceReview', params: { scanId: this.scan.id } });
    },

    // Download the master pipeline video (video-master/video.mp4) via a signed blob URL. Same
    // signed-anchor pattern the pipeline manager uses for the source file. Duallo-admin gated by
    // canDownloadMasterVideo (button visibility) — this is a convenience for review, not a mutation.
    async downloadMasterVideo() {
      if (this.downloadingMaster || !this.scan) return;
      this.downloadingMaster = true;
      try {
        const path = `/${this.scan.assetId}/scans/${this.scan.id}/video-master/video.mp4`;
        const signedUrl = await organizationSasService.getSignedUrl(path);
        if (!signedUrl) return;

        const a = document.createElement('a');
        a.href = signedUrl;
        a.target = '_blank';
        a.download = `${this.scan.name || this.scan.id}-master.mp4`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (error) {
        console.error('Error downloading master video:', error);
      } finally {
        this.downloadingMaster = false;
      }
    },

    getStageClass(stage) {
      switch (stage) {
        case "upload":
          return stageClass(sourceUploadStage(this.scan.status));
        case "source-processing":
          return stageClass(sourceProcessingStage(this.scan.status));
        case "pipeline-video":
          return stageClass(pipelineVideoStage(this.scan.status));
        case "model-generation":
          return stageClass(modelGenerationStage(this.scan.status));
        case "ai-analysis":
          return stageClass(aiAnalysisStage(this.scan.status, this.canDualloAdmin));
        case "overall":
          return stageClass(overallStage(this.scan.status, this.canDualloAdmin));
      }
    },

    getStageText(stage) {
      switch (stage) {
        case "upload":
          return sourceUploadStage(this.scan.status);
        case "source-processing":
          return sourceProcessingStage(this.scan.status);
        case "pipeline-video":
          return pipelineVideoStage(this.scan.status);
        case "model-generation":
          return modelGenerationStage(this.scan.status);
        case "ai-analysis":
          return aiAnalysisStage(this.scan.status, this.canDualloAdmin);
        case "overall":
          return overallStage(this.scan.status, this.canDualloAdmin);
      }
    },

    formatDate(value) {
      if (!value) return "—";

      try {
        return this.scan?.toLocalTime ? this.scan.toLocalTime(value) : value;
      } catch {
        return value;
      }
    },

    displayNumber(value) {
      if (value === null || value === undefined) return "—";
      return Number(value).toLocaleString();
    },

    // Camera type plus capture method (defaults to Walkthrough when not explicitly set).
    captureTypeLabel(scan) {
      const lens = Number(scan?.numberOfLenses) === 2 ? "360 Camera" : "Single Lens";
      const method = scan?.captureMethod || "Walkthrough";
      return `${lens} · ${method}`;
    },

    getNormalizedStatus(status) {
      return getScanStatusText(status);
    },

    async startGeneration() {
      const { confirm } = useConfirm();
      const confirmed = await confirm({
        title: "Add scan to queue?",
        message: "Processing will begin shortly and will use minutes from your plan.",
        confirmText: "Add to queue",
        icon: "mdi-cog-play-outline",
      });

      if (!confirmed) {
        return;
      }

      try {
        this.modelGenerationButtonClicked = true;
        const startRequest = new ModelGenerationRequest(
          this.organizationStore.organizationId,
          this.scan.assetId,
          this.scan.id
        );

        await ModelVersionService.startScanGeneration(startRequest);

        this.$emit('onGenerationStarted');
        this.closeWindow();
      } catch (error) {
        console.error('Error starting model generation:', error);
      }
    },

    closeWindow() {
      this.emitRefresh();
      this.$emit('onCloseWindow');
    }
  }
};
</script>

<style scoped>
.scan-details-dialog {
  color: #fff;
  padding: 4px;
}

.scan-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding-bottom: 14px;
}

.eyebrow {
  color: #8aa2ad;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.scan-title {
  color: var(--duallo-accent);
  font-weight: 700;
  font-size: 1.35rem;
  margin: 0;
}

.scan-subtitle {
  color: #afc0c8;
  font-size: 0.92rem;
  margin: 6px 0 0;
}

.status-wrap {
  flex-shrink: 0;
  padding-top: 4px;
}

.section-card {
  background: linear-gradient(180deg, #23323a 0%, #1f2c34 100%);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
}

.hero-card {
  border-color: rgba(100, 255, 218, 0.14);
}

.section-header {
  margin-bottom: 12px;
}

.section-title {
  color: #d6e4ea;
  font-weight: 600;
  font-size: 0.95rem;
}

.hero-message-title {
  color: #ffffff;
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 6px;
}

.hero-message-body {
  color: #b8c8cf;
  line-height: 1.5;
  font-size: 0.92rem;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.metric-card {
  background-color: rgba(255, 255, 255, 0.04);
  border-radius: 10px;
  padding: 12px;
}

.metric-label {
  color: #9fb2bb;
  font-size: 0.78rem;
  margin-bottom: 6px;
}

.metric-value {
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  word-break: break-word;
}

.timeline-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.timeline-row,
.stage-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.timeline-label,
.stage-name {
  color: #a8bbc3;
  font-size: 0.86rem;
}

.timeline-value {
  color: #fff;
  font-size: 0.9rem;
  text-align: right;
  word-break: break-word;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.78rem;
}

.stage-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.stage-badge {
  border-radius: 9999px;
  padding: 4px 10px;
  font-size: 0.76rem;
  font-weight: 700;
  min-width: 92px;
  text-align: center;
}

.stage-success {
  background-color: rgba(76, 175, 80, 0.18);
  color: #7ee083;
}

.stage-failed {
  background-color: rgba(244, 67, 54, 0.18);
  color: #ff8a80;
}

.stage-active {
  background-color: rgba(245, 124, 0, 0.18);
  color: #ffb45c;
}

.stage-queued {
  background-color: rgba(126, 87, 194, 0.18);
  color: #c5b3e6;
}

.stage-pending {
  background-color: rgba(96, 125, 139, 0.18);
  color: #b0bec5;
}

.stage-default {
  background-color: rgba(255, 255, 255, 0.08);
  color: #d6e4ea;
}

.actions-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mint-btn {
  background-color: var(--duallo-accent);
  color: #06211d;
  font-weight: 700;
  transition: 0.2s ease;
}

.mint-btn:hover {
  background-color: #4de0c0;
}

.cancel-btn {
  background-color: #37474f;
  color: #fff;
  font-weight: 600;
}

.review-inspect-btn {
  background-color: rgba(171, 71, 188, 0.2);
  color: #e1bee7;
  border: 1px solid rgba(171, 71, 188, 0.5);
  font-weight: 600;
}

.review-inspect-btn:hover {
  background-color: rgba(171, 71, 188, 0.35);
}

.download-master-btn {
  background-color: rgba(33, 150, 243, 0.16);
  color: #90caf9;
  border: 1px solid rgba(33, 150, 243, 0.45);
  font-weight: 600;
}

.download-master-btn:hover {
  background-color: rgba(33, 150, 243, 0.28);
}


@media (max-width: 960px) {
  .scan-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .timeline-row,
  .stage-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .timeline-value {
    text-align: left;
  }
}
</style>